import { useEffect, useMemo, useState } from "react";
import useStore from "../../../../useStore";
import Loader from "../../../Elements/Loader/Loader";
import Button from "../../../Elements/Button/Button";
import { useToast } from "../../../../Contexts/Toast/toast.provider";
import CustomCalendar from "../../../Elements/Calendar/Calendar";
import ReporteSemanalRepartidor from "../ReporteSemanalRepartidor/ReporteSemanalRepartidor";
import "./myOrders.css";
import { fmtCurrency } from "../../../../Utilities/Moneda/moneda";
import { fmtDate } from "../../../../Utilities/Fecha/fecha";
/* import { buildMapsUrl } from "../../../../Utilities/Maps/BuildUri"; */
import { badgeClassByStatus } from "../../../../Utilities/Status/statusColor";

function buildWhatsAppUrl(phone, customerName, orderId) {
  if (!phone) return null;
  // Normaliza número mexicano: quita espacios/guiones, agrega +52 si no tiene código
  const clean = phone.replace(/[\s\-().]/g, "");
  const normalized = clean.startsWith("+") ? clean : `+52${clean}`;
  const message = `Hola ${customerName || "cliente"}, estoy afuera de tu domicilio para la entrega de tu pedido #${orderId} 🛵`;
  return `https://wa.me/${normalized.replace("+", "")}?text=${encodeURIComponent(message)}`;
}

export default function MyOrders() {
  const showToast = useToast();
  const {
    myCourierOrders,
    loadingMyCourierOrders,
    errorMyCourierOrders,
    fetchDataCourierMyOrders,
    changeStatus,
    changePaymentStatus
  } = useStore();

  const [updating, setUpdating] = useState({});
  const [selectedDate, setSelectedDate] = useState(new Date());

  const orderDates = useMemo(() => {
    const set = new Set();
    if (myCourierOrders) {
      myCourierOrders.forEach((o) => {
        if (o.createdAt) {
          const d = new Date(o.createdAt);
          const key = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
          set.add(key);
        }
      });
    }
    return set;
  }, [myCourierOrders]);

  // DÍAS ATRASADOS (no entregado/abandonado y fecha < hoy)
  const overdueDates = useMemo(() => {
    const set = new Set();
    const today = new Date(); today.setHours(0, 0, 0, 0);
    (myCourierOrders || []).forEach((o) => {
      if (!o?.createdAt) return;
      const s = String(o?.status ?? "").trim().toLowerCase();
      const done = s === "entregado" || s === "abandonado";
      const d = new Date(o.createdAt);
      if (!done && d < today) {
        set.add(`${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`);
      }
    });
    return set;
  }, [myCourierOrders]);

  const filteredOrders = useMemo(() => {
    if (!myCourierOrders) return [];
    return myCourierOrders.filter((o) => {
      if (!o.createdAt) return false;
      const d = new Date(o.createdAt);
      return (
        d.getFullYear() === selectedDate.getFullYear() &&
        d.getMonth() === selectedDate.getMonth() &&
        d.getDate() === selectedDate.getDate()
      );
    });
  }, [myCourierOrders, selectedDate]);

  useEffect(() => {
    fetchDataCourierMyOrders();
  }, [fetchDataCourierMyOrders]);

  useEffect(() => {
    if (errorMyCourierOrders) showToast("error", errorMyCourierOrders);
  }, [errorMyCourierOrders, showToast]);

  const reload = () => fetchDataCourierMyOrders();

  const handleMarkDelivered = async (order) => {
    try {
      setUpdating((p) => ({ ...p, [order.orderId]: true }));

      // 1) Marcar como entregado
      const res1 = await changeStatus(order.restaurantId, order.orderId, "entregar");

      if (res1?.statusCode === 200) {
        showToast("success", `Pedido #${order.orderId} marcado como ENTREGADO ✅`);

        // 2) Marcar pago como pagado
        const res2 = await changePaymentStatus(order.restaurantId, order.orderId, "pagado");

        if (res2?.statusCode === 200) {
          showToast("success", `Pago del pedido #${order.orderId} marcado como PAGADO 💵`);
        } else {
          showToast("warning", `Pedido entregado, pero no se actualizó el pago.`);
        }

        await fetchDataCourierMyOrders();
      } else {
        const msg = res1?.message || res1?.body?.message || "No se pudo marcar como entregado.";
        showToast("error", msg);
      }
    } catch (e) {
      console.error(e);
      showToast("error", e?.message || "Error al marcar como entregado/pagado.");
    } finally {
      setUpdating((p) => ({ ...p, [order.orderId]: false }));
    }
  };

  const handleMarkAbandoned = async (order) => {
    try {
      setUpdating((p) => ({ ...p, [order.orderId]: true }));

      const res1 = await changeStatus(order.restaurantId, order.orderId, "abandonar");

      if (res1?.statusCode === 200) {
        showToast("success", `Pedido #${order.orderId} marcado como ABANDONADO 🚫`);

        const res2 = await changePaymentStatus(order.restaurantId, order.orderId, "abandonado");

        if (res2?.statusCode === 200) {
          showToast("info", `Pago del pedido #${order.orderId} marcado como ABANDONADO 💸`);
        } else {
          showToast("warning", `Pedido abandonado, pero no se actualizó el pago.`);
        }

        await fetchDataCourierMyOrders();
      } else {
        const msg = res1?.message || res1?.body?.message || "No se pudo marcar como abandonado.";
        showToast("error", msg);
      }
    } catch (e) {
      console.error(e);
      showToast("error", e?.message || "Error al marcar como abandonado/pago.");
    } finally {
      setUpdating((p) => ({ ...p, [order.orderId]: false }));
    }
  };

  return (
    <div className="container-fluid container-mh py-3 my-orders">
      {/* Header responsive con grid */}
      <div className="row align-items-center g-2 mb-3">
        <div className="col-12 col-lg">
          <h2 className="mb-0">📦 Mis pedidos asignados</h2>
          <div className="mb-3">
            <CustomCalendar
              value={selectedDate}
              onChange={setSelectedDate}
              orderDates={orderDates}
              pendingDates={overdueDates}
            />
          </div>
        </div>
        <div className="col-12 col-lg-auto">
          <div className="d-grid gap-2 d-sm-flex">
            <Button text="Refrescar" color="blue" fill action={reload} />
          </div>
        </div>
      </div>

      <ReporteSemanalRepartidor selectedDate={selectedDate} />

      {loadingMyCourierOrders && <Loader />}

      {!loadingMyCourierOrders && (
        <>
          {(!filteredOrders || filteredOrders.length === 0) ? (
            <div className="alert alert-warning text-center">
              ⚠️ No tienes pedidos este día.
            </div>
          ) : (
            <div className="row g-3">
              {filteredOrders
                .slice()
                .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
                .map((o) => {
                  const pickup = o?.pickup || {};
                  const entrega = o?.delivery || {};

                  /* const entregaUrl = buildMapsUrl(entrega);
                  const pickupUrl = buildMapsUrl(pickup); */
                  const routeUrl =
                    (Number.isFinite(pickup?.lat) && Number.isFinite(pickup?.lng) &&
                      Number.isFinite(entrega?.lat) && Number.isFinite(entrega?.lng))
                      ? `https://www.google.com/maps/dir/?api=1&origin=${pickup.lat},${pickup.lng}&destination=${entrega.lat},${entrega.lng}`
                      : null;

                  const tip = Number(o.tip || 0);
                  const isUpdating = !!updating[o.orderId];

                  return (
                    <div className="col-12 col-md-6 col-lg-4" key={o.orderId}>
                      <div className="card pr-card h-100 shadow-sm">
                        <div className="card-body d-flex flex-column">
                          <div className="d-flex justify-content-between align-items-start">
                            <h5 className="card-title mb-1">#{o.orderId}</h5>
                            <span className={`badge bg-${badgeClassByStatus(o.status)}`}>
                              {o.status}
                            </span>
                          </div>

                          <p className="text-muted mb-2">{fmtDate(o.createdAt)}</p>
                          <p className="mb-0">
                            <strong>{o.restauranteNombre || "Restaurante"}</strong>
                          </p>

                          {pickup?.direccion && (
                            <div className="mt-2">
                              <small className="text-muted d-block">Recoger en (Restaurante):</small>
                              <div className="delivery-address" title={pickup.direccion}>
                                {pickup.direccion}
                              </div>
                            </div>
                          )}

                          {entrega?.direccion && (
                            <div className="mt-2">
                              <small className="text-muted d-block">Entregar en (Cliente):</small>
                              <div className="delivery-address" title={entrega.direccion}>
                                {entrega.direccion}
                              </div>
                            </div>
                          )}

                          <hr className="my-2" />

                          <div className="d-flex justify-content-between">
                            <span>Subtotal</span>
                            <span>{fmtCurrency(o.subtotal)}</span>
                          </div>
                          <div className="d-flex justify-content-between">
                            <span>Envío</span>
                            <span>{fmtCurrency(o.shippingCost)}</span>
                          </div>
                          <div className="d-flex justify-content-between">
                            <span>Propina</span>
                            <span>{fmtCurrency(tip)}</span>
                          </div>
                          <div className="d-flex justify-content-between">
                            <span><strong>Total</strong></span>
                            <strong className="text-primary">{fmtCurrency(o.totalAmount)}</strong>
                          </div>

                          {/* Botonera responsive */}
                          <div className="mt-auto pt-3">
                            <div className="order-buttons">
                              {/*  {entregaUrl && (
                                <Button
                                  text="Ver destino"
                                  color="blue"
                                  fill
                                  action={() => window.open(entregaUrl, "_blank", "noopener,noreferrer")}
                                />
                              )}

                              {pickupUrl && (
                                <Button
                                  text="Ver entrega"
                                  color="gray"
                                  fill
                                  action={() => window.open(pickupUrl, "_blank", "noopener,noreferrer")}
                                />
                              )} */}

                              {routeUrl && (
                                <Button
                                  text="Ruta"
                                  color="purple"
                                  fill
                                  disabled={isUpdating || o.status !== "enviado"}
                                  action={() => window.open(routeUrl, "_blank", "noopener,noreferrer")}
                                />
                              )}

                              {(() => {
                                const waUrl = buildWhatsAppUrl(
                                  o.customer?.phone,
                                  o.customer?.name,
                                  o.orderId
                                );
                                return waUrl && o.status === "enviado" ? (
                                  <Button
                                    text="📲 Contactar cliente"
                                    color="blue"
                                    fill
                                    action={() => window.open(waUrl, "_blank", "noopener,noreferrer")}
                                  />
                                ) : null;
                              })()}

                              <Button
                                text={isUpdating ? "Marcando..." : "ENTREGADO"}
                                color="green"
                                fill
                                disabled={isUpdating || o.status !== "enviado"}
                                action={() => handleMarkDelivered(o)}
                              />

                              <Button
                                text={isUpdating ? "Marcando..." : "Cliente NO salió"}
                                color="orange"
                                fill
                                disabled={isUpdating || o.status !== "enviado"}
                                action={() => handleMarkAbandoned(o)}
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
            </div>
          )}
        </>
      )}
    </div>
  );
}